import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import * as moment from 'moment-timezone';
import { MessagesService } from '../../../service/messages-service/messages.service';
import { decryptField, encryptField } from '../../../utils/decrypt-utils';
import { sweetAlert } from '../../../utils/swal';

@Component({
  selector: 'app-messages',
  templateUrl: './messages.component.html',
  styleUrls: ['./messages.component.css']
})
export class MessagesComponent implements OnInit, OnDestroy {
  selectedUser: any = null;
  searchTerm = '';
  newMessageText = '';

  usersList: any[] = [];
  messagesList: any[] = [];
  isLoading = false;
  isLoadingMessages = false;
  errorMessage = '';
  isSending = false;
  @ViewChild('chatHistory', { static: false }) chatHistoryElement: ElementRef;

  constructor(
    private activatedRoute: ActivatedRoute,
    private messagesService: MessagesService
  ) { }

  ngOnInit(): void {
    this.activatedRoute.queryParams
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(params => {
        const reservationId = params['reservationId'] || '';
        this.loadMessagesData(reservationId);
      });
  }

  ngOnDestroy(): void { }

  loadMessagesData(reservationId: string | number): void {
    this.isLoading = true;
    this.errorMessage = '';

    // First API call: GET messages-contacts (general contact/thread list)
    this.messagesService.getMessagesContacts()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (contactsRes: any) => {
          console.log('GET messages-contacts response:', contactsRes);
          
          let parsedContacts = [];
          if (contactsRes && contactsRes.data) {
            parsedContacts = contactsRes.data;
          } else if (Array.isArray(contactsRes)) {
            parsedContacts = contactsRes;
          } else if (contactsRes && contactsRes.threads) {
            parsedContacts = contactsRes.threads;
          }

          this.usersList = this.parseThreads(parsedContacts);

          // Second API call: GET messages-contacts/thread/{reservationId} (if present)
          if (reservationId) {
            this.messagesService.getThread(reservationId)
              .pipe(takeUntil(componentDestroyed(this)))
              .subscribe(
                (threadRes: any) => {
                  console.log('GET messages-contacts/thread/' + reservationId + ' response:', threadRes);
                  
                  let threadData = [];
                  if (threadRes && threadRes.data) {
                    threadData = threadRes.data;
                  } else if (Array.isArray(threadRes)) {
                    threadData = threadRes;
                  } else if (threadRes && threadRes.thread) {
                    threadData = threadRes.thread;
                  }

                  const mappedThread = this.parseThreads(threadData);
                  
                  // Merge thread contacts into the general users list if not already present
                  for (let i = 0; i < mappedThread.length; i++) {
                    const tContact = mappedThread[i];
                    let exists = false;
                    for (let j = 0; j < this.usersList.length; j++) {
                      if (this.usersList[j].id === tContact.id) {
                        exists = true;
                        break;
                      }
                    }
                    if (!exists) {
                      this.usersList.unshift(tContact);
                    }
                  }

                  // Auto-select the contact matching the reservationId
                  if (mappedThread.length > 0) {
                    this.selectUser(mappedThread[0]);
                  } else if (this.usersList.length > 0) {
                    this.selectUser(this.usersList[0]);
                  }
                  
                  this.isLoading = false;
                },
                (threadError: any) => {
                  console.error('Error fetching thread for reservationId ' + reservationId + ':', threadError);
                  // Non-fatal error for thread API, select first user from main list if possible
                  if (this.usersList.length > 0) {
                    this.selectUser(this.usersList[0]);
                  }
                  this.isLoading = false;
                }
              );
          } else {
            // No reservationId, auto-select first contact from the list
            if (this.usersList.length > 0) {
              this.selectUser(this.usersList[0]);
            }
            this.isLoading = false;
          }
        },
        (error: any) => {
          console.error('Error fetching messages contacts:', error);
          this.errorMessage = 'Failed to load messages and contacts.';
          this.isLoading = false;
        }
      );
  }

  loadChatHistory(onboardingId: number | string, recipientId: number | string): void {
    if (!onboardingId || !recipientId) {
      this.messagesList = [];
      return;
    }
    
    this.isLoadingMessages = true;
    this.messagesService.getMessages(onboardingId, recipientId)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (res: any) => {
          let rawMessages = [];
          if (res && res.data) {
            rawMessages = res.data;
          } else if (Array.isArray(res)) {
            rawMessages = res;
          } else if (res && res.messages) {
            rawMessages = res.messages;
          }
          
          this.messagesList = this.parseMessages(rawMessages);
          this.isLoadingMessages = false;
          this.scrollToBottom();
        },
        (error: any) => {
          console.error('Error loading chat history:', error);
          this.messagesList = [];
          this.isLoadingMessages = false;
        }
      );
  }

  getRoleName(user?: any): string {
    if (!user) {
      return '';
    }

    if (user.placementagent_id != null) return 'Agent';
    if (user.facilitycontact_id != null) return 'Owner';
    if (user.patient_id != null) return 'Patient';
    if (user.responsibleparty_id != null) return 'Responsible Party';
    if (user.hospitalrepresentative_id != null) return 'Hospital Representative';
    if (user.facilityadministrator_id != null) return 'Facility Administrator';

    const typeId = user.publicusertype_id;

    if (typeId === 1) return 'Agent';
    if (typeId === 2) return 'Patient';
    if (typeId === 3) return 'Owner';
    if (typeId === 4) return 'Responsible Party';
    if (typeId === 5) return 'Hospital Representative';

    return '';
  }

  getInitials(firstName: string, lastName: string): string {
    const f = firstName && firstName.charAt(0) ? firstName.charAt(0).toUpperCase() : '';
    const l = lastName && lastName.charAt(0) ? lastName.charAt(0).toUpperCase() : '';
    return (f + l) || 'U';
  }

  parseThreads(threads: any[]): any[] {
    if (!threads || !Array.isArray(threads)) {
      return [];
    }

    // Get logged-in user placement ID from localStorage token payload
    let myId = 0;
    const currentToken = localStorage.getItem('placement_access_token');
    if (currentToken) {
      try {
        const payload = JSON.parse(atob(currentToken.split('.')[1]));
        myId = Number(payload.sub);
      } catch (e) {
        console.error('Error decoding placement access token payload:', e);
      }
    }

    const list: any[] = [];
    for (let i = 0; i < threads.length; i++) {
      const t = threads[i];
      if (!t) {
        continue;
      }
      
      let firstName = '';
      let lastName = '';
      if (t.e_publicusers && t.e_publicusers.myUser) {
        firstName = t.e_publicusers.myUser.first_name || '';
        lastName = t.e_publicusers.myUser.last_name || '';
      }
      const fullName = (firstName + ' ' + lastName).trim() || 'Unknown User';

      let facilityName = '';
      let referenceId = '';
      if (t.e_facilityintakeonboarding) {
        referenceId = t.e_facilityintakeonboarding.reference_id || '';
        if (t.e_facilityintakeonboarding.e_facilities) {
          facilityName = t.e_facilityintakeonboarding.e_facilities.name || '';
        }
      }

      const initials = this.getInitials(firstName, lastName);

      // Determine recipientId: contact_user_id represents selected contact/user directly
      let recipientId = t.contact_user_id;

      let role = '';
      if (t.e_publicusers) {
        role = this.getRoleName(t.e_publicusers);
      }

      list.push({
        id: t.id,
        recipientId: recipientId,
        onboardingId: t.facilityintakeonboarding_id,
        name: fullName,
        facilityName: facilityName,
        referenceId: referenceId,
        avatar: initials,
        time: '',
        role: role,
        raw: t
      });
    }
    return list;
  }

  parseMessages(rawMessages: any[]): any[] {
    if (!rawMessages || !Array.isArray(rawMessages)) {
      return [];
    }

    // Get logged-in user placement ID
    let myId = 0;
    const currentToken = localStorage.getItem('placement_access_token');
    if (currentToken) {
      try {
        const payload = JSON.parse(atob(currentToken.split('.')[1]));
        myId = Number(payload.sub);
      } catch (e) {
        console.error('Error decoding placement access token payload:', e);
      }
    }

    const parsed: any[] = [];
    for (let i = 0; i < rawMessages.length; i++) {
      const msg = rawMessages[i];
      if (!msg) {
        continue;
      }

      // Decrypt message content
      const decryptedText = decryptField(msg.message);

      // Determine sender
      const isMe = Number(msg.sender_id) === myId;
      const sender = isMe ? 'me' : 'other';

      // Format sent_at timestamp to h:mm A format (e.g. 10:09 AM)
      let formattedTime = '';
      if (msg.sent_at) {
        const m = moment(msg.sent_at);
        if (m.isValid()) {
          formattedTime = m.format('h:mm A');
        }
      }

      // Data safety - map and store only the required fields
      parsed.push({
        id: msg.id,
        senderId: msg.sender_id,
        recipientId: msg.recipient_id,
        text: decryptedText,
        time: formattedTime,
        rawSentAt: msg.sent_at,
        facilityIntakeOnboardingId: msg.facilityintakeonboarding_id,
        sender: sender
      });
    }

    // Sort messages in ascending chronological order based on sent_at
    parsed.sort((a, b) => {
      const dateA = a.rawSentAt ? new Date(a.rawSentAt).getTime() : 0;
      const dateB = b.rawSentAt ? new Date(b.rawSentAt).getTime() : 0;
      return dateA - dateB;
    });

    return parsed;
  }

  get filteredUsers() {
    const term = this.searchTerm ? this.searchTerm.toLowerCase().trim() : '';
    if (!term) {
      return this.usersList;
    }
    return this.usersList.filter(u => {
      const name = u.name ? u.name.toLowerCase() : '';
      const fac = u.facilityName ? u.facilityName.toLowerCase() : '';
      const ref = u.referenceId ? u.referenceId.toLowerCase() : '';
      return name.indexOf(term) !== -1 || fac.indexOf(term) !== -1 || ref.indexOf(term) !== -1;
    });
  }

  selectUser(user: any): void {
    if (this.selectedUser && this.selectedUser.id === user.id) {
      return;
    }
    this.selectedUser = user;
    this.messagesList = []; // Cleanly reset/replace chat messages list to prevent leakage
    if (user && user.onboardingId && user.recipientId) {
      this.loadChatHistory(user.onboardingId, user.recipientId);
    }
  }

  sendMessage(): void {
    if (!this.newMessageText || !this.newMessageText.trim() || !this.selectedUser) {
      return;
    }

    if (this.isSending) {
      return;
    }

    const text = this.newMessageText.trim();
    const recipientId = this.selectedUser.recipientId;
    const onboardingId = this.selectedUser.onboardingId;

    if (!recipientId || !onboardingId) {
      console.warn('Selected contact user does not have a valid recipientId or onboardingId');
      return;
    }

    this.isSending = true;

    // Encrypt the message text before sending
    const encryptedMessage = encryptField(text);

    this.messagesService.sendMessage(onboardingId, recipientId, encryptedMessage)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (res: any) => {
          this.newMessageText = '';
          this.isSending = false;
          // Refresh the conversation history
          this.loadChatHistory(onboardingId, recipientId);
        },
        (error: any) => {
          console.error('Error sending message:', error);
          this.isSending = false;
          sweetAlert('Error', 'Failed to send message.', 'warning', 'OK');
        }
      );
  }

  scrollToBottom(): void {
    setTimeout(() => {
      try {
        if (this.chatHistoryElement && this.chatHistoryElement.nativeElement) {
          const el = this.chatHistoryElement.nativeElement;
          el.scrollTop = el.scrollHeight;
        }
      } catch (err) {
        console.error('Error scrolling to bottom:', err);
      }
    }, 100);
  }
}
